export type BrainPocketArticle = {
  id: string;
  title: string;
  url: string;
  savedAt: string;
  read: boolean;
  keyPoints: string[];
};

export type BrainPocketDomain = {
  articles: BrainPocketArticle[];
};

export type BrainPocketStore = {
  domains: Record<string, BrainPocketDomain>;
};

const STORAGE_KEY = 'brainpocket';

export const emptyStore: BrainPocketStore = {
  domains: {},
};

function isArticle(value: unknown): value is BrainPocketArticle {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const article = value as Partial<BrainPocketArticle>;
  return typeof article.id === 'string'
    && typeof article.title === 'string'
    && typeof article.url === 'string'
    && typeof article.savedAt === 'string'
    && typeof article.read === 'boolean'
    && Array.isArray(article.keyPoints)
    && article.keyPoints.every((point) => typeof point === 'string');
}

export function validateImportData(data: unknown): data is BrainPocketStore {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const maybeStore = data as Partial<BrainPocketStore>;
  if (!maybeStore.domains || typeof maybeStore.domains !== 'object') {
    return false;
  }

  return Object.values(maybeStore.domains).every((domain) => (
    !!domain
    && typeof domain === 'object'
    && Array.isArray((domain as BrainPocketDomain).articles)
    && (domain as BrainPocketDomain).articles.every(isArticle)
  ));
}

export function loadStore(): BrainPocketStore {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return emptyStore;
    }

    const parsed = JSON.parse(raw);
    return validateImportData(parsed) ? parsed : emptyStore;
  } catch {
    return emptyStore;
  }
}

export function saveStore(store: BrainPocketStore) {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

export function exportStore(store: BrainPocketStore) {
  const blob = new Blob([JSON.stringify(store, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = `brainpocket-backup-${new Date().toISOString().slice(0, 10)}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}
